import { useState } from 'react';
import foodTrackingService from '../../services/foodTrackingService';

interface AddProductModalProps {
  onClose: () => void;
  onSuccess: (productId: string) => void;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date?: string;
  onDirectAdd?: () => void;
}

type AddMode = 'select' | 'manual' | 'ai';

interface AIIngredient {
  name: string;
  defaultGrams: number;
  isPiece: boolean;
  pieceLabel: string | null;
  gramsPerPiece: number | null;
  defaultPieces: number | null;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber?: number | null;
  sugar?: number | null;
  sodium?: number | null;
}

interface AIResult {
  isComposite: boolean;
  mealName: string;
  ingredients: AIIngredient[];
  servingUnit: string;
  category: string;
}

interface IngredientState {
  grams: number;
  pieces: number;
  piecesManuallySet?: boolean;
}

export default function AddProductModal({ 
  onClose, 
  onSuccess, 
  mealType, 
  date, 
  onDirectAdd 
}: AddProductModalProps) {
  const [mode, setMode] = useState<AddMode>('select');
  const [loading, setLoading] = useState(false);
  
  // Manual form
  const [manualForm, setManualForm] = useState({
    name: '',
    brand: '',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    fiber: '',
    sugar: '',
    sodium: '',
    servingSize: '100',
    servingUnit: 'g'
  });
  
  // AI form
  const [aiForm, setAiForm] = useState({
    name: '',
    brand: ''
  });
  const [aiResult, setAiResult] = useState<AIResult | null>(null);
  const [ingredientStates, setIngredientStates] = useState<{ [index: number]: IngredientState }>({});

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const nutrition = {
        calories: parseFloat(manualForm.calories),
        protein: parseFloat(manualForm.protein),
        carbs: parseFloat(manualForm.carbs),
        fat: parseFloat(manualForm.fats),
        fiber: manualForm.fiber ? parseFloat(manualForm.fiber) : undefined,
        sugar: manualForm.sugar ? parseFloat(manualForm.sugar) : undefined,
        sodium: manualForm.sodium ? parseFloat(manualForm.sodium) : undefined,
      };
      const servingSizeValue = parseFloat(manualForm.servingSize);

      if (mealType && date && onDirectAdd) {
        // Build a proper ISO datetime with timezone offset so the server stores the correct UTC time
        const now = new Date();
        const [year, month, day] = date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
        const consumedAt = localDate.toISOString();

        await foodTrackingService.createEntry({
          foodName: manualForm.name,
          brand: manualForm.brand || undefined,
          servingSize: servingSizeValue,
          servingUnit: manualForm.servingUnit,
          quantity: servingSizeValue,
          nutrition,
          mealType,
          date: consumedAt,
        });
        
        onDirectAdd();
        onClose();
        return;
      }

      const result = await foodTrackingService.createCustomFood({
        name: manualForm.name,
        brand: manualForm.brand || undefined,
        servingSize: servingSizeValue,
        servingUnit: manualForm.servingUnit,
        nutrition,
      });
      
      onSuccess(result.id!);
    } catch (error: any) {
      console.error('Manual submit error:', error);
      alert(error.message || 'Eroare la adăugarea produsului');
    } finally {
      setLoading(false);
    }
  };

  const handleAiComplete = async () => {
    if (!aiForm.name.trim()) {
      alert('Te rog introdu numele produsului');
      return;
    }
    
    setLoading(true);
    try {
      const result = await foodTrackingService.getNutritionFromAI(
        aiForm.name,
        aiForm.brand || undefined
      );
      
      const aiData: AIResult = {
        isComposite: result.isComposite || false,
        mealName: result.mealName || aiForm.name,
        ingredients: (result.ingredients || []).map((ing: any) => ({
          name: ing.name,
          defaultGrams: ing.defaultGrams || 100,
          isPiece: ing.isPiece || false,
          pieceLabel: ing.pieceLabel || null,
          gramsPerPiece: ing.gramsPerPiece ? parseFloat(String(ing.gramsPerPiece)) : null,
          defaultPieces: ing.defaultPieces ? parseFloat(String(ing.defaultPieces)) : null,
          calories: parseFloat(String(ing.calories ?? 0)) || 0,
          protein: parseFloat(String(ing.protein ?? 0)) || 0,
          carbs: parseFloat(String(ing.carbs ?? 0)) || 0,
          fats: parseFloat(String(ing.fats ?? ing.fat ?? 0)) || 0,
          fiber: ing.fiber != null ? parseFloat(String(ing.fiber)) : null,
          sugar: ing.sugar != null ? parseFloat(String(ing.sugar)) : null,
          sodium: ing.sodium != null ? parseFloat(String(ing.sodium)) : null,
        })),
        servingUnit: result.servingUnit || 'g',
        category: result.category || 'general',
      };
      
      // If no ingredients returned (backward compat), create single ingredient
      if (aiData.ingredients.length === 0) {
        aiData.ingredients = [{
          name: aiForm.name,
          defaultGrams: 100,
          isPiece: false,
          pieceLabel: null,
          gramsPerPiece: null,
          defaultPieces: null,
          calories: parseFloat(String(result.calories ?? 0)) || 0,
          protein: parseFloat(String(result.protein ?? 0)) || 0,
          carbs: parseFloat(String(result.carbs ?? 0)) || 0,
          fats: parseFloat(String(result.fats ?? result.fat ?? 0)) || 0,
          fiber: result.fiber != null ? parseFloat(String(result.fiber)) : null,
          sugar: result.sugar != null ? parseFloat(String(result.sugar)) : null,
          sodium: result.sodium != null ? parseFloat(String(result.sodium)) : null,
        }];
      }
      
      setAiResult(aiData);
      
      // Initialize states with default values
      const initialStates: { [index: number]: IngredientState } = {};
      aiData.ingredients.forEach((ing, idx) => {
        initialStates[idx] = {
          grams: ing.defaultGrams,
          pieces: ing.isPiece && ing.defaultPieces ? ing.defaultPieces : 1,
        };
      });
      setIngredientStates(initialStates);
    } catch (error: any) {
      alert(error.message || 'Eroare la completarea automată');
    } finally {
      setLoading(false);
    }
  };

  const updateIngredientGrams = (index: number, newGrams: number) => {
    const safeGrams = Math.max(0, newGrams);
    setIngredientStates(prev => {
      const current = prev[index] || { grams: 0, pieces: 1 };
      // When user adjusts grams directly, DO NOT recalculate pieces.
      // Pieces count is only changed via the +/- piece buttons.
      // This way: if user has 3 bananas and edits grams from 360g to 300g,
      // pieces stay at 3 (user is just fine-tuning the actual weight).
      return {
        ...prev,
        [index]: {
          ...current,
          grams: safeGrams,
          pieces: current.pieces,
        }
      };
    });
  };


  const updateIngredientPieces = (index: number, newPieces: number) => {
    const ing = aiResult?.ingredients[index];
    const safePieces = Math.max(0, newPieces);
    setIngredientStates(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        pieces: safePieces,
        grams: ing?.gramsPerPiece ? Math.round(safePieces * ing.gramsPerPiece) : prev[index]?.grams || 0,
        piecesManuallySet: true,
      }
    }));
  };

  const getIngredientNutrition = (ingredient: AIIngredient, grams: number) => {
    const factor = grams / 100;
    return {
      calories: Math.round(ingredient.calories * factor),
      protein: Math.round(ingredient.protein * factor * 10) / 10,
      carbs: Math.round(ingredient.carbs * factor * 10) / 10,
      fat: Math.round(ingredient.fats * factor * 10) / 10,
      fiber: ingredient.fiber != null ? Math.round(ingredient.fiber * factor * 10) / 10 : undefined,
      sugar: ingredient.sugar != null ? Math.round(ingredient.sugar * factor * 10) / 10 : undefined,
      sodium: ingredient.sodium != null ? Math.round(ingredient.sodium * factor * 10) / 10 : undefined,
    };
  };

  const getTotalNutrition = () => {
    if (!aiResult) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    return aiResult.ingredients.reduce((totals, ing, idx) => {
      const grams = ingredientStates[idx]?.grams || 0;
      const nutrition = getIngredientNutrition(ing, grams);
      return {
        calories: totals.calories + nutrition.calories,
        protein: Math.round((totals.protein + nutrition.protein) * 10) / 10,
        carbs: Math.round((totals.carbs + nutrition.carbs) * 10) / 10,
        fat: Math.round((totals.fat + nutrition.fat) * 10) / 10,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const getTotalGrams = () => {
    if (!aiResult) return 0;
    return aiResult.ingredients.reduce((total, _, idx) => {
      return total + (ingredientStates[idx]?.grams || 0);
    }, 0);
  };

  const handleAiSubmit = async () => {
    if (!aiResult) return;
    
    setLoading(true);
    try {
      const brandValue = aiForm.brand || undefined;

      if (mealType && date && onDirectAdd) {
        // Build a proper ISO datetime with timezone offset so the server stores the correct UTC time
        const now = new Date();
        const [year, month, day] = date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
        const consumedAt = localDate.toISOString();

        for (let i = 0; i < aiResult.ingredients.length; i++) {
          const ing = aiResult.ingredients[i];
          const grams = ingredientStates[i]?.grams || 0;
          if (grams <= 0) continue;
          
          const nutrition = getIngredientNutrition(ing, grams);
          const pieces = ingredientStates[i]?.pieces || 0;
          
          // Build descriptive food name for piece items
          let foodName = ing.name;
          if (ing.isPiece && pieces > 0) {
            foodName = `${ing.name} (${pieces}${pieces === 1 ? ' buc' : ' buc'})`;
          }
          
          await foodTrackingService.createEntry({
            foodName,
            brand: brandValue,
            servingSize: grams,
            servingUnit: aiResult.servingUnit || 'g',
            quantity: grams,
            nutrition,
            mealType,
            date: consumedAt,
            notes: aiResult.isComposite ? `Din: ${aiResult.mealName}` : undefined,
          });
        }
        
        onDirectAdd();
        onClose();
        return;
      }

      const totalNutrition = getTotalNutrition();
      const totalGrams = getTotalGrams();
      
      const result = await foodTrackingService.createCustomFood({
        name: aiResult.mealName || aiForm.name,
        brand: brandValue,
        servingSize: totalGrams,
        servingUnit: aiResult.servingUnit || 'g',
        nutrition: totalNutrition,
      });
      
      onSuccess(result.id!);
    } catch (error: any) {
      console.error('AI submit error:', error);
      alert(error.message || 'Eroare la salvarea produsului');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-hidden">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            {mode === 'select' && 'Adaugă Produs Nou'}
            {mode === 'manual' && 'Adaugă Manual'}
            {mode === 'ai' && 'Adaugă cu AI'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {mode === 'select' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-6">
                Alege modul în care vrei să adaugi produsul:
              </p>
              
              {/* AI Option */}
              <button
                onClick={() => setMode('ai')}
                className="w-full p-6 border-2 border-primary-300 rounded-lg hover:bg-primary-50 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">Adaugă cu AI (Recomandat)</h3>
                    <p className="text-sm text-gray-600">
                      Scrie doar numele produsului și AI-ul va completa automat toate valorile nutriționale
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-primary-600 font-medium">
                      <span>✨ Rapid și simplu</span>
                      <span>•</span>
                      <span>🎯 Date precise</span>
                      <span>•</span>
                      <span>🍽️ Separă ingredientele</span>
                    </div>
                  </div>
                </div>
              </button>

              {/* Manual Option */}
              <button
                onClick={() => setMode('manual')}
                className="w-full p-6 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">Adaugă Manual</h3>
                    <p className="text-sm text-gray-600">
                      Completează tu toate câmpurile cu valorile nutriționale cunoscute
                    </p>
                    <div className="mt-2 text-xs text-gray-500">
                      📝 Control total • 🎓 Educativ
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {mode === 'ai' && !aiResult && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Cum funcționează?</p>
                    <p>AI-ul va analiza numele produsului. Dacă este un preparat compus (ex: "piept de pui cu orez"), va separa automat ingredientele. Pentru alimente la bucată (ex: "2 banane"), estimează automat gramajul.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Produs *
                </label>
                <input
                  type="text"
                  value={aiForm.name}
                  onChange={(e) => setAiForm({ ...aiForm, name: e.target.value })}
                  placeholder="Ex: 2 banane, Piept de pui cu orez, 3 ouă..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand (opțional)
                </label>
                <input
                  type="text"
                  value={aiForm.brand}
                  onChange={(e) => setAiForm({ ...aiForm, brand: e.target.value })}
                  placeholder="Ex: Danone, Milka, Nestle..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setMode('select')}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={loading}
                >
                  Înapoi
                </button>
                <button
                  onClick={handleAiComplete}
                  disabled={loading || !aiForm.name.trim()}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generez...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>Completează cu AI</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {mode === 'ai' && aiResult && (() => {
            const totalNutrition = getTotalNutrition();
            const totalGrams = getTotalGrams();
            
            return (
              <div className="space-y-5">
                {/* Success banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">
                      {aiResult.isComposite 
                        ? `AI a separat "${aiResult.mealName}" în ${aiResult.ingredients.length} ingrediente!`
                        : 'AI a completat datele nutriționale!'
                      }
                    </span>
                  </div>
                </div>

                {/* Meal name for composite */}
                {aiResult.isComposite && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">🍽️</span>
                      <h3 className="font-bold text-purple-900 text-lg">{aiResult.mealName}</h3>
                    </div>
                    <p className="text-sm text-purple-700">
                      Setează gramajul individual pentru fiecare ingredient:
                    </p>
                  </div>
                )}

                {/* Individual ingredients */}
                <div className="space-y-3">
                  {aiResult.ingredients.map((ingredient, idx) => {
                    const state = ingredientStates[idx] || { grams: 0, pieces: 1 };
                    const ingNutrition = getIngredientNutrition(ingredient, state.grams);
                    
                    return (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        {/* Ingredient header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{ingredient.name}</h4>
                            {ingredient.isPiece && ingredient.pieceLabel ? (
                              <p className="text-xs text-amber-600 font-medium mt-0.5">
                                🔸 {ingredient.pieceLabel} ≈ {ingredient.gramsPerPiece}g
                              </p>
                            ) : (
                              <p className="text-xs text-gray-400">
                                per 100g: {ingredient.calories} kcal · {ingredient.protein}g P · {ingredient.carbs}g C · {ingredient.fats}g F
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Piece-based control */}
                        {ingredient.isPiece && ingredient.gramsPerPiece ? (
                          <div className="space-y-3 mb-3">
                            {/* Pieces selector */}
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-xs font-medium text-amber-700 mb-2 text-center">Câte bucăți?</p>
                              <div className="flex items-center justify-center gap-4">
                                <button
                                  type="button"
                                  onClick={() => updateIngredientPieces(idx, state.pieces - 1)}
                                  className="w-10 h-10 rounded-full bg-red-100 text-red-600 font-bold text-xl flex items-center justify-center hover:bg-red-200 transition-colors flex-shrink-0"
                                >
                                  −
                                </button>
                                <div className="text-center">
                                  <span className="text-2xl font-bold text-gray-800">{state.pieces}</span>
                                  <span className="text-sm text-gray-500 ml-1">buc</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateIngredientPieces(idx, state.pieces + 1)}
                                  className="w-10 h-10 rounded-full bg-green-100 text-green-600 font-bold text-xl flex items-center justify-center hover:bg-green-200 transition-colors flex-shrink-0"
                                >
                                  +
                                </button>
                              </div>
                              <p className="text-xs text-amber-600 text-center mt-2 font-medium">
                                {state.pieces} × {ingredient.pieceLabel} ≈ {state.grams}g
                              </p>
                            </div>
                            
                            {/* Fine-tune grams */}
                            <div>
                              <p className="text-xs text-gray-400 mb-1.5">Ajustare fină:</p>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateIngredientGrams(idx, state.grams - 5)}
                                  className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 font-bold text-sm flex items-center justify-center hover:bg-gray-300 transition-colors flex-shrink-0"
                                >
                                  −
                                </button>
                                <div className="flex-1 relative">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={state.grams === 0 ? '' : state.grams}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/[^0-9]/g, '');
                                      updateIngredientGrams(idx, raw === '' ? 0 : parseInt(raw, 10));
                                    }}
                                    placeholder="0"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center font-bold text-sm"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">g</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => updateIngredientGrams(idx, state.grams + 5)}
                                  className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 font-bold text-sm flex items-center justify-center hover:bg-gray-300 transition-colors flex-shrink-0"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Standard grams control */
                          <div className="flex items-center gap-3 mb-3">
                            <button
                              type="button"
                              onClick={() => updateIngredientGrams(idx, state.grams - 10)}
                              className="w-9 h-9 rounded-full bg-red-100 text-red-600 font-bold text-lg flex items-center justify-center hover:bg-red-200 transition-colors flex-shrink-0"
                            >
                              −
                            </button>
                            <div className="flex-1 relative">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={state.grams === 0 ? '' : state.grams}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9]/g, '');
                                  updateIngredientGrams(idx, raw === '' ? 0 : parseInt(raw, 10));
                                }}
                                placeholder="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center font-bold text-lg"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">g</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => updateIngredientGrams(idx, state.grams + 10)}
                              className="w-9 h-9 rounded-full bg-green-100 text-green-600 font-bold text-lg flex items-center justify-center hover:bg-green-200 transition-colors flex-shrink-0"
                            >
                              +
                            </button>
                          </div>
                        )}
                        
                        {/* Per-ingredient nutrition */}
                        <div className="grid grid-cols-4 gap-1.5">
                          <div className="text-center bg-orange-50 rounded-lg py-1.5 px-1">
                            <div className="text-sm font-bold text-orange-600">{ingNutrition.calories}</div>
                            <div className="text-[10px] text-gray-500">kcal</div>
                          </div>
                          <div className="text-center bg-blue-50 rounded-lg py-1.5 px-1">
                            <div className="text-sm font-bold text-blue-600">{ingNutrition.protein}g</div>
                            <div className="text-[10px] text-gray-500">Prot</div>
                          </div>
                          <div className="text-center bg-yellow-50 rounded-lg py-1.5 px-1">
                            <div className="text-sm font-bold text-yellow-600">{ingNutrition.carbs}g</div>
                            <div className="text-[10px] text-gray-500">Carbo</div>
                          </div>
                          <div className="text-center bg-red-50 rounded-lg py-1.5 px-1">
                            <div className="text-sm font-bold text-red-600">{ingNutrition.fat}g</div>
                            <div className="text-[10px] text-gray-500">Grăsimi</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* TOTAL SECTION */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-bold text-gray-700">
                      📊 Total Masă
                    </p>
                    <span className="text-xs bg-white px-2 py-1 rounded-full text-gray-500 font-medium">
                      {totalGrams}g total
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-xl font-bold text-orange-600">{totalNutrition.calories}</div>
                      <div className="text-xs text-gray-500">kcal</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-blue-600">{totalNutrition.protein}g</div>
                      <div className="text-xs text-gray-500">Prot</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-yellow-600">{totalNutrition.carbs}g</div>
                      <div className="text-xs text-gray-500">Carbo</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-red-600">{totalNutrition.fat}g</div>
                      <div className="text-xs text-gray-500">Grăsimi</div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => { setAiResult(null); setMode('select'); setIngredientStates({}); }}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    disabled={loading}
                  >
                    Anulează
                  </button>
                  <button
                    onClick={handleAiSubmit}
                    disabled={loading || totalGrams === 0}
                    className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-bold"
                  >
                    {loading ? 'Salvez...' : '✓ Adaugă la Masă'}
                  </button>
                </div>
              </div>
            );
          })()}

          {mode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Produs *
                </label>
                <input
                  type="text"
                  value={manualForm.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand (opțional)
                </label>
                <input
                  type="text"
                  value={manualForm.brand}
                  onChange={(e) => setManualForm({ ...manualForm, brand: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mărime porție *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={manualForm.servingSize}
                    onChange={(e) => setManualForm({ ...manualForm, servingSize: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unitate *
                  </label>
                  <select
                    value={manualForm.servingUnit}
                    onChange={(e) => setManualForm({ ...manualForm, servingUnit: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                    disabled={loading}
                  >
                    <option value="g">g (grame)</option>
                    <option value="ml">ml (mililitri)</option>
                    <option value="bucată">bucată</option>
                  </select>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Valori Nutriționale (per 100g/ml)</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calorii (kcal) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.calories}
                      onChange={(e) => setManualForm({ ...manualForm, calories: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Proteine (g) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.protein}
                      onChange={(e) => setManualForm({ ...manualForm, protein: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Carbohidrați (g) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.carbs}
                      onChange={(e) => setManualForm({ ...manualForm, carbs: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grăsimi (g) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.fats}
                      onChange={(e) => setManualForm({ ...manualForm, fats: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fibre (g)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.fiber}
                      onChange={(e) => setManualForm({ ...manualForm, fiber: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zaharuri (g)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.sugar}
                      onChange={(e) => setManualForm({ ...manualForm, sugar: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sodiu (mg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={manualForm.sodium}
                      onChange={(e) => setManualForm({ ...manualForm, sodium: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setMode('select')}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={loading}
                >
                  Înapoi
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {loading ? 'Salvez...' : 'Salvează Produs'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
